import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, CheckCircle, AlertCircle, Lightbulb, Flag, MessageSquare, Pin } from 'lucide-react';
import { Button } from '../ui';
import { NoteType, CreateNoteRequest } from '../../services/workNotes';
import './AddNoteForm.css';

interface AddNoteFormProps {
  onSubmit: (data: CreateNoteRequest) => Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  defaultNoteType?: NoteType;
  placeholder?: string;
  compact?: boolean;
}

const noteTypeOptions: { value: NoteType; label: string; icon: React.ReactNode; color: string }[] = [
  { value: 'General', label: 'Note', icon: <MessageSquare size={14} />, color: 'default' },
  { value: 'Milestone', label: 'Milestone', icon: <Flag size={14} />, color: 'success' },
  { value: 'Issue', label: 'Issue', icon: <AlertCircle size={14} />, color: 'danger' },
  { value: 'Idea', label: 'Idea', icon: <Lightbulb size={14} />, color: 'warning' },
  { value: 'StatusChange', label: 'Status', icon: <CheckCircle size={14} />, color: 'info' },
];

export const AddNoteForm: React.FC<AddNoteFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting = false,
  defaultNoteType = 'General',
  placeholder,
  compact = false,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(!compact);
  const [content, setContent] = useState('');
  const [noteType, setNoteType] = useState<NoteType>(defaultNoteType);
  const [isPinned, setIsPinned] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const defaultPlaceholder = placeholder || t('notes.placeholder');

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${Math.min(Math.max(scrollHeight, 120), 400)}px`;
    }
  }, [content]);

  // Focus textarea when expanded
  useEffect(() => {
    if (isExpanded && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isExpanded]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || isSubmitting) return;

    try {
      await onSubmit({
        content: content.trim(),
        noteType,
        isPinned,
      });
      setContent('');
      setNoteType(defaultNoteType);
      setIsPinned(false);
      if (compact) {
        setIsExpanded(false);
      }
    } catch (error) {
      // Error handling done by parent
    }
  };

  const handleCancel = () => {
    setContent('');
    setNoteType(defaultNoteType);
    setIsPinned(false);
    if (compact) {
      setIsExpanded(false);
    }
    onCancel?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Submit on Cmd/Ctrl + Enter
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (compact && !isExpanded) {
    return (
      <button
        className="add-note-form__trigger"
        onClick={() => setIsExpanded(true)}
      >
        <Plus size={16} />
        <span>{t('notes.addNote')}</span>
      </button>
    );
  }

  const selectedType = noteTypeOptions.find(opt => opt.value === noteType);

  return (
    <form className="add-note-form" onSubmit={handleSubmit}>
      {/* Textarea - prominent position */}
      <div className="add-note-form__editor">
        <textarea
          ref={textareaRef}
          className="add-note-form__textarea"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={defaultPlaceholder}
          disabled={isSubmitting}
        />
        <div className="add-note-form__char-hint">
          {content.length > 0 && (
            <span className={content.length > 4500 ? 'add-note-form__char-hint--warning' : ''}>
              {content.length}/5000
            </span>
          )}
          <span className="add-note-form__keyboard-hint">⌘+Enter to save</span>
        </div>
      </div>

      {/* Options bar */}
      <div className="add-note-form__options">
        <div className="add-note-form__left-options">
          {/* Note type selector */}
          <div className="add-note-form__type-dropdown">
            <button
              type="button"
              className={`add-note-form__type-trigger add-note-form__type-trigger--${selectedType?.color}`}
              onClick={(e) => {
                const menu = e.currentTarget.nextElementSibling;
                menu?.classList.toggle('add-note-form__type-menu--open');
              }}
            >
              {selectedType?.icon}
              <span>{selectedType?.label}</span>
            </button>
            <div className="add-note-form__type-menu">
              {noteTypeOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`add-note-form__type-option add-note-form__type-option--${option.color} ${noteType === option.value ? 'add-note-form__type-option--selected' : ''}`}
                  onClick={(e) => {
                    setNoteType(option.value);
                    e.currentTarget.parentElement?.classList.remove('add-note-form__type-menu--open');
                  }}
                >
                  {option.icon}
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Pin toggle */}
          <button
            type="button"
            className={`add-note-form__pin-btn ${isPinned ? 'add-note-form__pin-btn--active' : ''}`}
            onClick={() => setIsPinned(!isPinned)}
            title={isPinned ? t('notes.unpinNote') : t('notes.pinNote')}
          >
            <Pin size={14} />
            {isPinned && <span>{t('notes.sections.pinned')}</span>}
          </button>
        </div>

        {/* Action buttons */}
        <div className="add-note-form__actions">
          {(compact || onCancel) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              {t('notes.actions.cancel')}
            </Button>
          )}
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={!content.trim() || isSubmitting}
          >
            {isSubmitting ? t('notes.actions.saving') : t('notes.actions.save')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default AddNoteForm;
